'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSportTopPlayer,
  deleteSportTopPlayer,
  listSportTopPlayers,
  updateSportTopPlayer,
  type CreateSportTopPlayerInput,
  type UpdateSportTopPlayerInput,
} from '@nomiss/services';

export const adminSportTopPlayersQueryKeys = {
  all: ['admin', 'sport-top-players'] as const,
  list: (sportCode: string) =>
    [...adminSportTopPlayersQueryKeys.all, sportCode] as const,
};

export function useAdminSportTopPlayers(
  sportCode: string,
  options?: { enabled?: boolean },
) {
  const enabled = (options?.enabled ?? true) && sportCode.length > 0;

  const adminSportTopPlayersQuery = useQuery({
    queryKey: adminSportTopPlayersQueryKeys.list(sportCode),
    queryFn: () => listSportTopPlayers(sportCode),
    enabled,
  });

  return { adminSportTopPlayersQuery };
}

export function useCreateSportTopPlayer() {
  const queryClient = useQueryClient();

  const createSportTopPlayerMutation = useMutation({
    mutationFn: (input: CreateSportTopPlayerInput) =>
      createSportTopPlayer(input),
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({
        queryKey: adminSportTopPlayersQueryKeys.list(input.sport_code),
      });
    },
  });

  return { createSportTopPlayerMutation };
}

export function useUpdateSportTopPlayer(sportCode: string) {
  const queryClient = useQueryClient();

  const updateSportTopPlayerMutation = useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: UpdateSportTopPlayerInput;
    }) => updateSportTopPlayer(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminSportTopPlayersQueryKeys.list(sportCode),
      });
    },
  });

  return { updateSportTopPlayerMutation };
}

export function useDeleteSportTopPlayer(sportCode: string) {
  const queryClient = useQueryClient();

  const deleteSportTopPlayerMutation = useMutation({
    mutationFn: (id: string) => deleteSportTopPlayer(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminSportTopPlayersQueryKeys.list(sportCode),
      });
    },
  });

  return { deleteSportTopPlayerMutation };
}
